/**
 * Timeline playback runtime state - a shared, per-TimelineLauncher-item-id
 * mirror of the live sim state TimelineLauncher.tsx already computes for
 * itself (playing/armed/position/current section/progress). Exists so a
 * DisplayItem bound to `source: 'timeline'` can read another item's
 * playback state without owning it - same purpose and shape as
 * `sequencer/runtime.ts` / `macros/runtime.ts`, just fed by a local
 * component effect instead of a bridge broadcast.
 *
 * [CORRECTED 2026-10-11 (CP-184): this said "Timeline playback is a pure
 * client-side sim, there is no bridge engine for it yet". Untrue for a long
 * while: a timeline PLAYS on OPBridge, on its home bridge (DEC-224), and the
 * launcher's sim is only the on-screen view of it, reconciled against
 * `statePoll.ts`. What this module holds is still that local view, written
 * only by a mounted TimelineLauncher. For "is a song playing anywhere, and
 * where" use `statePoll.ts` (and `countIn.ts`, which follows every bridge).]
 */

import { createSignal, type Accessor } from 'solid-js';

export interface TimelineRuntimeState {
  playing: boolean;
  armed: boolean;
  posBars: number;
  /** Current section/step id, or null when not launched / no section. */
  activeSectionId: string | null;
  /** 0..100 progress within the whole timeline (matches TimelineLauncher's
   *  own `playheadPct()` scale - a percentage, not a 0..1 fraction). */
  playheadPct: number;
  /** "bar.beat" readout, matching TimelineLauncher's own display. */
  barReadout: string;
}

const [runtimeSignal, setRuntimeSignal] = createSignal<Record<string, TimelineRuntimeState>>({});

export const timelineRuntimeStates: Accessor<Record<string, TimelineRuntimeState>> = runtimeSignal;

/** Called by TimelineLauncher.tsx's own beat-driven effect on every change.
 *  Pure write-out - does not read back or drive TimelineLauncher itself. */
export function recordTimelineRuntime(itemId: string, state: TimelineRuntimeState): void {
  setRuntimeSignal((prev) => ({ ...prev, [itemId]: state }));
}

/** Called by TimelineLauncher.tsx on unmount so a removed launcher doesn't
 *  leave a bound Display reading a stale ghost state - a missing entry
 *  renders as "not launched," never a frozen last-known value. */
export function clearTimelineRuntime(itemId: string): void {
  setRuntimeSignal((prev) => {
    if (!(itemId in prev)) return prev;
    const next = { ...prev };
    delete next[itemId];
    return next;
  });
}
